import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ethers } from "ethers";
import { getInjectedProvider, getEthersProvider, hasInjectedWallet } from "../web3/provider";
import { switchToActiveNetwork } from "../web3/switchNetwork";
import { ACTIVE_NETWORK } from "../config/app.config";

const Web3Context = createContext(null);

export function Web3Provider({ children }) {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [booting, setBooting] = useState(true);

  // Guard so we attach wallet event listeners exactly once.
  const listenersBound = useRef(false);

  const isCorrectNetwork = chainId !== null && chainId === ACTIVE_NETWORK.chainId;
  const hasWallet = hasInjectedWallet();

  const readChainId = useCallback(async () => {
    const injected = getInjectedProvider();
    if (!injected) return null;
    try {
      const hex = await injected.request({ method: "eth_chainId" });
      return parseInt(hex, 16);
    } catch {
      return null;
    }
  }, []);

  // Connect wallet (explicit user action).
  const connect = useCallback(async () => {
    const injected = getInjectedProvider();
    if (!injected) throw new Error("NO_WALLET");
    setConnecting(true);
    try {
      const accounts = await injected.request({ method: "eth_requestAccounts" });
      const cid = await readChainId();
      setAccount(accounts && accounts[0] ? ethers.utils.getAddress(accounts[0]) : null);
      setChainId(cid);
      return accounts && accounts[0];
    } finally {
      setConnecting(false);
    }
  }, [readChainId]);

  const disconnect = useCallback(() => {
    // Injected wallets can't be force-disconnected; clear local state only.
    setAccount(null);
  }, []);

  // Switch to the correct network. State updates via the chainChanged
  // listener below — no page reload, so React state/UI is preserved.
  const switchNetwork = useCallback(async () => {
    setSwitching(true);
    try {
      await switchToActiveNetwork();
      const cid = await readChainId();
      setChainId(cid);
    } finally {
      setSwitching(false);
    }
  }, [readChainId]);

  // One-time: eager-connect if already authorized + bind wallet events.
  useEffect(() => {
    let cancelled = false;
    const injected = getInjectedProvider();

    async function boot() {
      if (!injected) {
        if (!cancelled) setBooting(false);
        return;
      }
      try {
        const accounts = await injected.request({ method: "eth_accounts" });
        const cid = await readChainId();
        if (!cancelled) {
          if (accounts && accounts[0]) setAccount(ethers.utils.getAddress(accounts[0]));
          setChainId(cid);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setBooting(false);
      }
    }

    boot();

    if (injected && injected.on && !listenersBound.current) {
      listenersBound.current = true;

      const onAccounts = (accts) => {
        setAccount(accts && accts[0] ? ethers.utils.getAddress(accts[0]) : null);
      };
      // IMPORTANT: update state, do NOT reload the page on chain change.
      const onChain = (hexId) => {
        setChainId(parseInt(hexId, 16));
      };

      injected.on("accountsChanged", onAccounts);
      injected.on("chainChanged", onChain);

      // Cleanup on unmount.
      return () => {
        cancelled = true;
        if (injected.removeListener) {
          injected.removeListener("accountsChanged", onAccounts);
          injected.removeListener("chainChanged", onChain);
        }
        listenersBound.current = false;
      };
    }

    return () => {
      cancelled = true;
    };
  }, [readChainId]);

  // Fresh provider/signer derived from current account + chain.
  const provider = useMemo(() => {
    if (!account) return null;
    return getEthersProvider();
  }, [account, chainId]);

  const signer = useMemo(() => {
    if (!provider || !account) return null;
    try {
      return provider.getSigner();
    } catch {
      return null;
    }
  }, [provider, account]);

  const value = {
    account,
    chainId,
    provider,
    signer,
    hasWallet,
    connecting,
    switching,
    booting,
    isConnected: !!account,
    isCorrectNetwork,
    connect,
    disconnect,
    switchNetwork,
    targetChainId: ACTIVE_NETWORK.chainId,
    targetNetworkName: ACTIVE_NETWORK.name,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}

export function useWeb3() {
  const ctx = useContext(Web3Context);
  if (!ctx) throw new Error("useWeb3 must be used within Web3Provider");
  return ctx;
}
